/**
 * Move Reason Constants (Frontend)
 * Mirror of supabase/functions/_shared/moveReasonConstants.ts for frontend use
 */

export const PREFERENCE_SIGNAL_REASONS = [
  'skill_match',
  'tech_preference',
  'customer_request',
  'specialty_match',
] as const;

export const OPERATIONAL_REASONS = [
  'auto_cascade',
  'parking_lot_rollover',
  'weather_block',
  'bay_closure',
  'bay_gating',
  'capacity_balance',
  'no_past_time',
  'tech_absent',
  'shift_conflict',
  'break_conflict',
  'rollover_split',
  'system_optimization',
  'capacity_recovery_pull_forward', // Core-100 CORE-BO
  'other',
] as const;

export type PreferenceSignalReason = typeof PREFERENCE_SIGNAL_REASONS[number];
export type OperationalReason = typeof OPERATIONAL_REASONS[number];
export type MoveReason = PreferenceSignalReason | OperationalReason;

// Core-100 R4 W5 (CORE-BO F): mirror of the server's closed vocabulary (parity-tested in
// tests/unit/core100/r4MoveReasonVocabulary.test.ts). reassign-ticket refuses anything else.
export const SURFACE_MOVE_REASONS = [
  'manual-kebab-tech-change',
  'manual-kebab-date-change',
  'manual-kebab-to-parking-lot',
  'optiplace-reassign',
  'optimizer-suggestion',
  'optimizer-apply',
  'optimizer-undo',
  'optimizer-defer',
  'optimize-board-commit',
  'import-initial-assign',
  'import-no-tech-available',
  'specialty-auto-assign',
  'due-out-mitigation',
  'auto-bump-split-cascade',
] as const;

export type SurfaceMoveReason = typeof SURFACE_MOVE_REASONS[number];

// WS-ABSENT (2026-10-20, CORE-AB:43 / CORE-AD:80 / FD-S3): the absent-technician flow's codes are retired. It committed
// smart-assign's #1 ('absent-tech-reassign', recorded automatic) or parked the ticket ('absent-tech-no-available',
// '-time-expired', '-reassign-failed') with no person choosing and no shop enablement. Marking a technician absent now
// opens a review where a person confirms each move with the kebab's human reasons; a stale browser is refused.
export const RETIRED_MOVE_REASONS = [
  'present-vehicle-priority',
  'absent-tech-reassign',
  'absent-tech-no-available',
  'absent-tech-time-expired',
  'absent-tech-reassign-failed',
] as const;

export const MOVE_REASON_VOCABULARY: readonly string[] = [
  ...PREFERENCE_SIGNAL_REASONS,
  ...OPERATIONAL_REASONS,
  ...SURFACE_MOVE_REASONS,
];

export function isMoveReasonInVocabulary(reason: unknown): boolean {
  return typeof reason === 'string' && MOVE_REASON_VOCABULARY.includes(reason);
}

export function isPreferenceSignalReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return PREFERENCE_SIGNAL_REASONS.includes(reason as PreferenceSignalReason);
}

export const ALL_MOVE_REASONS: Array<{ value: MoveReason; label: string; isLearning: boolean }> = [
  { value: 'skill_match', label: 'Skill Match', isLearning: true },
  { value: 'tech_preference', label: 'Tech Preference', isLearning: true },
  { value: 'customer_request', label: 'Customer Request', isLearning: true },
  { value: 'specialty_match', label: 'Specialty Match', isLearning: true },
  { value: 'capacity_balance', label: 'Capacity Balance', isLearning: false },
  { value: 'capacity_recovery_pull_forward', label: 'Capacity Recovery (Pull Forward)', isLearning: false },
  { value: 'other', label: 'Other', isLearning: false },
];
